/**
 * Word 导出服务
 * 使用 docx 库将 Markdown 内容导出为 Word 文档
 */

import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, Table, TableRow, TableCell, VerticalAlign } from 'docx'
import { marked } from 'marked'
import { ExportOptions } from '../../../types/export'
import { downloadBlob } from '../../../utils/download'
import { generateExportFilename } from './exportStyles'

/**
 * 解析 HTML 文本为文本运行数组
 * 支持 <strong>, <em>, <code>, <a>, <del> 等标签
 */
const parseHtmlText = (html: string): TextRun[] => {
  const runs: TextRun[] = []

  // 创建一个临时容器来解析 HTML
  const container = document.createElement('div')
  container.innerHTML = html

  const processNode = (node: Node) => {
    if (node.nodeType === 3) {
      // 文本节点
      const text = node.textContent || ''
      if (text) {
        runs.push(new TextRun({ text }))
      }
    } else if (node.nodeType === 1) {
      // 元素节点
      const element = node as HTMLElement
      const tag = element.tagName.toLowerCase()
      const childNodes = Array.from(node.childNodes)

      if (tag === 'strong' || tag === 'b') {
        childNodes.forEach(child => {
          if (child.nodeType === 3) {
            const text = child.textContent || ''
            if (text) runs.push(new TextRun({ text, bold: true }))
          } else {
            processNode(child)
          }
        })
      } else if (tag === 'em' || tag === 'i') {
        childNodes.forEach(child => {
          if (child.nodeType === 3) {
            const text = child.textContent || ''
            if (text) runs.push(new TextRun({ text, italics: true }))
          } else {
            processNode(child)
          }
        })
      } else if (tag === 'code') {
        const text = element.textContent || ''
        if (text) {
          runs.push(new TextRun({
            text,
            font: 'Consolas',
            size: 20,
            color: 'E83E8C',
          }))
        }
      } else if (tag === 'a') {
        const text = element.textContent || ''
        if (text) {
          runs.push(new TextRun({
            text,
            color: '0366D6',
            underline: {},
          }))
        }
      } else if (tag === 'del' || tag === 's') {
        childNodes.forEach(child => {
          if (child.nodeType === 3) {
            const text = child.textContent || ''
            if (text) runs.push(new TextRun({ text, strike: true }))
          } else {
            processNode(child)
          }
        })
      } else {
        // 处理其他元素中的内容
        childNodes.forEach(child => processNode(child))
      }
    }
  }

  processNode(container)
  return runs.length > 0 ? runs : [new TextRun({ text: '' })]
}

/**
 * 解析 Markdown 内容为 Word 文档段落
 */
const parseMarkdownToParagraphs = async (content: string): Promise<(Paragraph | Table)[]> => {
  marked.setOptions({
    breaks: true,
    gfm: true,
  })

  const paragraphs: (Paragraph | Table)[] = []
  const html = await marked.parse(content)

  // 创建临时容器来解析 HTML
  const container = document.createElement('div')
  container.innerHTML = html

  const childNodes = Array.from(container.childNodes)

  for (const node of childNodes) {
    if (node.nodeType !== 1) continue

    const element = node as HTMLElement
    const tag = element.tagName.toLowerCase()

    // 处理标题
    if (tag.match(/^h[1-6]$/)) {
      const level = parseInt(tag[1])
      const text = element.innerHTML
      const runs = parseHtmlText(text)

      let headingLevel
      switch (level) {
        case 1: headingLevel = HeadingLevel.HEADING_1; break
        case 2: headingLevel = HeadingLevel.HEADING_2; break
        case 3: headingLevel = HeadingLevel.HEADING_3; break
        case 4: headingLevel = HeadingLevel.HEADING_4; break
        case 5: headingLevel = HeadingLevel.HEADING_5; break
        default: headingLevel = HeadingLevel.HEADING_6; break
      }

      paragraphs.push(
        new Paragraph({
          children: runs,
          heading: headingLevel,
          spacing: { before: 400, after: 200 },
        })
      )
    }
    // 处理段落
    else if (tag === 'p') {
      const text = element.innerHTML
      const runs = parseHtmlText(text)
      paragraphs.push(
        new Paragraph({
          children: runs,
          spacing: { after: 200 },
        })
      )
    }
    // 处理无序列表
    else if (tag === 'ul') {
      const listItems = Array.from(element.querySelectorAll('li'))
      listItems.forEach(li => {
        const text = li.innerHTML
        const runs = parseHtmlText(text)
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({ text: '• ' }),
              ...runs,
            ],
            spacing: { after: 100 },
            indent: { left: 400 },
          })
        )
      })
    }
    // 处理有序列表
    else if (tag === 'ol') {
      const listItems = Array.from(element.querySelectorAll('li'))
      listItems.forEach((li, index) => {
        const text = li.innerHTML
        const runs = parseHtmlText(text)
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({ text: `${index + 1}. ` }),
              ...runs,
            ],
            spacing: { after: 100 },
            indent: { left: 400 },
          })
        )
      })
    }
    // 处理引用块
    else if (tag === 'blockquote') {
      const text = element.innerHTML
      const runs = parseHtmlText(text)
      paragraphs.push(
        new Paragraph({
          children: runs,
          border: {
            left: { style: BorderStyle.SINGLE, size: 24, color: 'CCCCCC' },
          },
          indent: { left: 400 },
          spacing: { after: 200 },
        })
      )
    }
    // 处理代码块
    else if (tag === 'pre') {
      const codeElement = element.querySelector('code')
      const text = codeElement?.textContent || element.textContent || ''
      paragraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text,
              font: 'Consolas',
              size: 20,
              color: '333333',
            }),
          ],
          border: {
            top: { style: BorderStyle.SINGLE, size: 1 },
            bottom: { style: BorderStyle.SINGLE, size: 1 },
            left: { style: BorderStyle.SINGLE, size: 1 },
            right: { style: BorderStyle.SINGLE, size: 1 },
          },
          shading: { fill: 'F6F8FA' },
          spacing: { before: 200, after: 200 },
          indent: { left: 200, right: 200 },
        })
      )
    }
    // 处理表格
    else if (tag === 'table') {
      const rows: TableRow[] = []
      const tableRows = Array.from(element.querySelectorAll('tr'))

      tableRows.forEach((tr) => {
        const cells: TableCell[] = []
        const tds = Array.from(tr.querySelectorAll('td, th'))

        tds.forEach(td => {
          const text = td.innerHTML
          const runs = parseHtmlText(text)
          const isHeader = td.tagName.toLowerCase() === 'th'

          cells.push(
            new TableCell({
              children: [
                new Paragraph({
                  children: runs.length > 0 ? runs : [new TextRun({ text: '' })],
                  spacing: { after: 100 },
                }),
              ],
              shading: isHeader ? { fill: 'F6F8FA' } : undefined,
              verticalAlign: VerticalAlign.CENTER,
            })
          )
        })

        rows.push(new TableRow({ children: cells }))
      })

      paragraphs.push(
        new Table({
          rows,
          width: { size: 100, type: 'pct' },
        })
      )
    }
    // 处理水平线
    else if (tag === 'hr') {
      paragraphs.push(
        new Paragraph({
          border: {
            bottom: { style: BorderStyle.SINGLE, size: 6, color: 'CCCCCC' },
          },
          spacing: { before: 200, after: 200 },
        })
      )
    }
  }

  return paragraphs
}

/**
 * 导出为 Word 文档
 */
export const exportToWord = async (
  content: string,
  options: ExportOptions
): Promise<void> => {
  try {
    const contentElements = await parseMarkdownToParagraphs(content)

    // 创建文档
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            // 添加标题
            ...(options.title ? [
              new Paragraph({
                children: [
                  new TextRun({
                    text: options.title,
                    bold: true,
                    size: 36,
                  }),
                ],
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
                spacing: { after: 400 },
              }),
            ] : []),
            // 添加内容段落和表格
            ...contentElements,
          ],
        },
      ],
    })

    // 生成 blob
    const blob = await Packer.toBlob(doc)

    // 下载文件
    const filename = generateExportFilename('docx', options.filename)
    downloadBlob(blob, filename)

  } catch (error) {
    console.error('Word 导出失败:', error)
    throw new Error(`Word 导出失败: ${error instanceof Error ? error.message : '未知错误'}`)
  }
}

/**
 * 预览 Word 文档（返回 blob URL）
 */
export const previewWord = async (
  content: string,
  options: ExportOptions
): Promise<string> => {
  try {
    const contentElements = await parseMarkdownToParagraphs(content)

    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            ...(options.title ? [
              new Paragraph({
                children: [
                  new TextRun({
                    text: options.title,
                    bold: true,
                    size: 36,
                  }),
                ],
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
                spacing: { after: 400 },
              }),
            ] : []),
            ...contentElements,
          ],
        },
      ],
    })

    const blob = await Packer.toBlob(doc)
    return URL.createObjectURL(blob)

  } catch (error) {
    console.error('Word 预览失败:', error)
    throw new Error(`Word 预览失败: ${error instanceof Error ? error.message : '未知错误'}`)
  }
}

/**
 * 检查 Word 导出支持
 */
export const checkWordSupport = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof document !== 'undefined' && 
         typeof Blob !== 'undefined' &&
         typeof URL !== 'undefined'
}